/**
 * ToolFeedbackDrawer — right-side drawer that shows the full feedback
 * thread for one tool. Admin can post a reply, mark notes resolved /
 * unresolved, and delete notes.
 */

import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  X, Send, CheckCircle2, RotateCcw, Trash2, MessageSquare, User as UserIcon,
  ArrowUp,
} from 'lucide-react';

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return d.toLocaleDateString();
}

// Same sort the server uses: unresolved first, upvotes desc, newest first.
// We re-apply after an optimistic upvote flip so hot ideas immediately
// re-position without waiting on a refetch.
function sortThread(rows) {
  return [...rows].sort((a, b) => {
    if (Boolean(a.resolved) !== Boolean(b.resolved)) return a.resolved ? 1 : -1;
    const ua = a.upvote_count || 0;
    const ub = b.upvote_count || 0;
    if (ua !== ub) return ub - ua;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });
}

export default function ToolFeedbackDrawer({ tool, onClose, onCountsChange }) {
  const { API } = useContext(AuthContext);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tool) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/tools/${tool.id}/feedback`);
        if (!cancelled) setThread(sortThread(data?.feedback || []));
      } catch {
        if (!cancelled) toast.error('Failed to load feedback');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, tool]);

  const emitCounts = (nextThread) => {
    if (!onCountsChange) return;
    const total = nextThread.length;
    const unresolved = nextThread.filter((n) => !n.resolved).length;
    onCountsChange({ feedback_count: total, unresolved_feedback: unresolved });
  };

  const postReply = async () => {
    const text = reply.trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/tools/${tool.id}/feedback`, { message: text });
      const next = sortThread([data, ...thread]);
      setThread(next);
      emitCounts(next);
      setReply('');
      toast.success('Reply posted to thread');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post reply');
    } finally {
      setSaving(false);
    }
  };

  const toggleResolved = async (note) => {
    try {
      const { data } = await axios.put(
        `${API}/admin/tools/${tool.id}/feedback/${note.id}`,
        { resolved: !note.resolved },
      );
      const next = sortThread(thread.map((n) => (n.id === note.id ? { ...n, ...data } : n)));
      setThread(next);
      emitCounts(next);
    } catch (e) {
      toast.error('Could not update note');
    }
  };

  const toggleUpvote = async (note) => {
    // Optimistic flip so the UI feels instant, then reconcile with server.
    const nowUpvoted = !note.upvoted_by_me;
    const optimistic = thread.map((n) => n.id === note.id
      ? { ...n, upvoted_by_me: nowUpvoted, upvote_count: Math.max(0, (n.upvote_count || 0) + (nowUpvoted ? 1 : -1)) }
      : n);
    setThread(sortThread(optimistic));
    try {
      const { data } = await axios.post(`${API}/admin/tools/${tool.id}/feedback/${note.id}/upvote`);
      setThread((prev) => sortThread(prev.map((n) => (n.id === note.id ? { ...n, ...data } : n))));
    } catch (e) {
      // Revert on failure.
      setThread(sortThread(thread));
      toast.error('Could not update upvote');
    }
  };

  const deleteNote = async (note) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/tools/${tool.id}/feedback/${note.id}`);
      const next = thread.filter((n) => n.id !== note.id);
      setThread(next);
      emitCounts(next);
      toast.success('Note deleted');
    } catch (e) {
      toast.error('Could not delete note');
    }
  };

  if (!tool) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-background/70 backdrop-blur flex justify-end"
      onClick={onClose}
      data-testid="tool-feedback-drawer"
    >
      <div
        className="w-full max-w-md h-full bg-background border-l border-border shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
              Feedback
            </div>
            <div className="font-bold text-base mt-0.5 truncate">{tool.name}</div>
            <div className="text-xs text-slate-500">{thread.length} note{thread.length === 1 ? '' : 's'} · {thread.filter(n => !n.resolved).length} unresolved</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-foreground shrink-0"
            data-testid="tool-feedback-drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center text-sm text-slate-500 py-10">Loading thread…</div>
          ) : thread.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10 flex flex-col items-center gap-2">
              <MessageSquare className="w-8 h-8 opacity-40" />
              <div>No notes yet. Beta testers&apos; feedback will show up here.</div>
            </div>
          ) : (
            thread.map((note) => (
              <div
                key={note.id}
                className={`border rounded-lg p-3 space-y-2 ${
                  note.resolved
                    ? 'border-emerald-300/50 bg-emerald-50/50 dark:bg-emerald-950/20'
                    : 'border-border bg-muted/30'
                }`}
                data-testid={`feedback-note-${note.id}`}
              >
                <div className="flex items-start justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <UserIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="font-semibold truncate">{note.user_email || 'Unknown'}</span>
                    {note.user_role ? (
                      <span className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-[10px] uppercase tracking-wider">
                        {note.user_role}
                      </span>
                    ) : null}
                  </div>
                  <span className="text-slate-500 shrink-0">{relativeTime(note.created_at)}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">{note.message}</div>
                {note.resolved ? (
                  <div className="text-[10px] text-emerald-700 dark:text-emerald-400">
                    Resolved by {note.resolved_by} · {relativeTime(note.resolved_at)}
                  </div>
                ) : null}
                <div className="flex items-center justify-between gap-1 pt-1">
                  <Button
                    size="sm"
                    variant={note.upvoted_by_me ? 'default' : 'outline'}
                    className={`h-7 px-2 text-xs ${
                      note.upvoted_by_me ? 'bg-primary text-primary-foreground' : ''
                    }`}
                    onClick={() => toggleUpvote(note)}
                    data-testid={`feedback-upvote-${note.id}`}
                    title={note.upvoted_by_me ? 'Remove your upvote' : 'Upvote — hot ideas float to the top'}
                  >
                    <ArrowUp className="w-3 h-3 mr-1" />
                    <span className="font-bold">{note.upvote_count || 0}</span>
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => toggleResolved(note)}
                      data-testid={`feedback-toggle-${note.id}`}
                    >
                      {note.resolved ? (
                        <><RotateCcw className="w-3 h-3 mr-1" /> Reopen</>
                      ) : (
                        <><CheckCircle2 className="w-3 h-3 mr-1" /> Resolve</>
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-rose-600"
                      onClick={() => deleteNote(note)}
                      data-testid={`feedback-delete-${note.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reply composer */}
        <div className="border-t border-border p-3 shrink-0 space-y-2">
          <Textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply as admin, or drop your own note on this tool…"
            data-testid="feedback-reply-textarea"
          />
          <div className="flex justify-end">
            <Button
              onClick={postReply}
              disabled={saving || !reply.trim()}
              size="sm"
              data-testid="feedback-reply-submit"
            >
              <Send className="w-4 h-4 mr-1.5" /> {saving ? 'Posting…' : 'Post reply'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
